import React, { useRef, useEffect, useCallback, useMemo } from "react";

/**
 * BlazeAI animated logo.
 *
 * Idle  — thin teal ring + two dot eyes with an occasional blink.
 * Working — eyes look side-to-side & blink, canvas flame contour ring.
 *
 * Props:
 *   size        (number)  rendered px, default 24
 *   isWorking   (boolean) false = idle, true = working
 *   showRing    (boolean) show the outer teal ring, default true
 *   color       (string)  teal color for ring and eyes
 *   flameBoost  (number)  multiplier on flame tongue height, default 1.
 *                         Use >1 for larger instances (e.g. 1.4 for big hero avatars).
 */

const UNIQUE = "blazeai-logo";

// Inject keyframes once
let stylesInjected = false;
function injectStyles() {
  if (stylesInjected) return;
  stylesInjected = true;
  const css = `
@keyframes ${UNIQUE}-idleBlink {
  0%   { transform: scaleY(1); }
  90%  { transform: scaleY(1); }
  93%  { transform: scaleY(0.05); }
  96%  { transform: scaleY(1); }
  100% { transform: scaleY(1); }
}
@keyframes ${UNIQUE}-lookBlink {
  0%   { transform: translateX(0)     scaleY(1); }
  10%  { transform: translateX(10px)  scaleY(1); }
  25%  { transform: translateX(10px)  scaleY(1); }
  28%  { transform: translateX(10px)  scaleY(0.05); }
  31%  { transform: translateX(10px)  scaleY(1); }
  45%  { transform: translateX(0)     scaleY(1); }
  55%  { transform: translateX(-10px) scaleY(1); }
  70%  { transform: translateX(-10px) scaleY(1); }
  73%  { transform: translateX(-10px) scaleY(0.05); }
  76%  { transform: translateX(-10px) scaleY(1); }
  90%  { transform: translateX(0)     scaleY(1); }
  100% { transform: translateX(0)     scaleY(1); }
}`;
  const el = document.createElement("style");
  el.textContent = css;
  document.head.appendChild(el);
}

// ─── Flame contour engine (ported from the HTML prototype) ────────
class FlameEngine {
  constructor(canvas, ringR, flameScale = 1) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");
    this.w = canvas.width;
    this.h = canvas.height;
    this.cx = this.w / 2;
    this.cy = this.h / 2;
    this.ringR = ringR;
    this.flameScale = flameScale;
    this.time = Math.random() * 100;
    this.tongues = [];
    const count = 28;
    for (let i = 0; i < count; i++) {
      this.tongues.push({
        angle: (i / count) * Math.PI * 2,
        baseH: (18 + Math.random() * 30) * flameScale,
        speed: 1.5 + Math.random() * 2.5,
        phase: Math.random() * Math.PI * 2,
        width: 0.08 + Math.random() * 0.06,
      });
    }
  }

  draw() {
    const { ctx, w, h } = this;
    ctx.clearRect(0, 0, w, h);
    this.time += 0.025;
    const t = this.time;
    const b = Math.max(4, 14 * this.flameScale);
    this._layer(ctx, t, 1.4, "rgba(10,186,181,0.1)", b);
    this._layer(ctx, t, 1.0, "rgba(10,186,181,0.3)", 0);
    this._layer(ctx, t, 0.55, "rgba(140,255,248,0.18)", 0);
  }

  _layer(ctx, t, hMul, color, blur) {
    ctx.save();
    if (blur > 0) ctx.filter = `blur(${blur}px)`;
    ctx.fillStyle = color;
    ctx.beginPath();
    const steps = 120;
    for (let i = 0; i <= steps; i++) {
      const a = (i / steps) * Math.PI * 2;
      let fh = 0;
      for (const tg of this.tongues) {
        let d = a - tg.angle;
        if (d > Math.PI) d -= Math.PI * 2;
        if (d < -Math.PI) d += Math.PI * 2;
        const inf = Math.exp(-(d * d) / (2 * tg.width * tg.width));
        const fl = Math.sin(t * tg.speed + tg.phase) * 0.3 + 0.7;
        const wv = Math.sin(t * 1.2 + a * 3) * 0.15 + 0.85;
        fh += tg.baseH * inf * fl * wv * hMul;
      }
      const r = this.ringR + fh;
      const px = this.cx + Math.cos(a) * r;
      const py = this.cy + Math.sin(a) * r;
      i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
    }
    for (let i = steps; i >= 0; i--) {
      const a = (i / steps) * Math.PI * 2;
      const px = this.cx + Math.cos(a) * (this.ringR - 3);
      const py = this.cy + Math.sin(a) * (this.ringR - 3);
      ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }
}

// ─── Component ────────────────────────────────────────────────────
const FLAME_THRESHOLD = 28; // only render canvas flames above this size
const CANVAS_SCALE = 3; // canvas pixel multiplier for crisp flames

export default function BlazeAILogo({
  size = 24,
  isWorking = true,
  showRing = true,
  color = "#2A9A94",
  flameBoost = 1,
}) {
  injectStyles();

  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const rafRef = useRef(null);
  const showFlames = isWorking && size >= FLAME_THRESHOLD;

  // Canvas dimensions — grow with flameBoost so larger flames have room to render.
  const canvasCSS = size * Math.max(1.5, 1.5 * flameBoost);
  const canvasPx = Math.round(canvasCSS * CANVAS_SCALE);

  // Ring radius in canvas pixel space:
  // SVG is `size` CSS px with viewBox 512, ring at r=190.
  // Canvas pixel size = CSS size * CANVAS_SCALE.
  const ringRCanvas = useMemo(
    () => (size / 512) * 190 * CANVAS_SCALE,
    [size]
  );

  const startLoop = useCallback(() => {
    const engine = engineRef.current;
    if (!engine) return;
    const tick = () => {
      engine.draw();
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const stopLoop = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext("2d");
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
  }, []);

  useEffect(() => {
    if (!showFlames) {
      stopLoop();
      engineRef.current = null;
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = canvasPx;
    canvas.height = canvasPx;
    // Scale flames relative to a "reference" size of 100px, then apply flameBoost.
    const flameScale = Math.min(1, size / 100) * flameBoost;
    engineRef.current = new FlameEngine(canvas, ringRCanvas, flameScale);
    startLoop();
    return stopLoop;
  }, [showFlames, canvasPx, ringRCanvas, size, flameBoost, startLoop, stopLoop]);

  const eyeAnim = isWorking
    ? `${UNIQUE}-lookBlink 4s cubic-bezier(0.4,0,0.2,1) infinite`
    : `${UNIQUE}-idleBlink 5s ease-in-out infinite`;

  const canvasOffset = (canvasCSS - size) / 2;

  return (
    <span
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: size,
        flexShrink: 0,
      }}
    >
      {/* Canvas flame layer (behind SVG) */}
      {showFlames && (
        <canvas
          ref={canvasRef}
          style={{
            position: "absolute",
            top: -canvasOffset,
            left: -canvasOffset,
            width: canvasCSS,
            height: canvasCSS,
            pointerEvents: "none",
          }}
        />
      )}

      {/* SVG logo */}
      <svg
        viewBox="0 0 512 512"
        width={size}
        height={size}
        fill="none"
        style={{ position: "relative", zIndex: 1, overflow: "visible" }}
      >
        {/* Thin ring (optional) */}
        {showRing && (
          <circle
            cx="256"
            cy="256"
            r="190"
            stroke={color}
            strokeWidth={size < 20 ? "24" : size < 40 ? "16" : "12"}
            fill="none"
          />
        )}
        {/* Left eye */}
        <g transform="translate(200,236)">
          <circle
            r={size < 20 ? "24" : "18"}
            fill={color}
            style={{ animation: eyeAnim }}
          />
        </g>
        {/* Right eye */}
        <g transform="translate(312,236)">
          <circle
            r={size < 20 ? "24" : "18"}
            fill={color}
            style={{ animation: eyeAnim }}
          />
        </g>
      </svg>
    </span>
  );
}
